import { List as ImList, Map as ImMap } from 'immutable';
import { DateTime } from 'luxon';

import { transactionsUtils } from 'companion-app-components/flux/transactions';
import type { CashFlowTransaction } from 'companion-app-components/flux/transactions/transactionsTypes';
import type { Account } from 'companion-app-components/flux/accounts/accountsTypes';
import type { Category } from 'companion-app-components/flux/categories/categoriesTypes';

import { noNaN } from 'utils/utils';
import { getTxnStateInfo, RefundState, getRefundState, isBankOwnedPending, isC2RTransaction } from 'data/transactions/utils';
import { memoizeWithKey, memoizeWithKeyRetrieveCache } from 'utils/memoizeWithKey';
import { getOrCreateStringFromObject } from 'utils/objectToKeyUtils';
import { TransactionListUtilProps } from './types';

/*
 CALC RUNNING BALANCES
 */
export function calcRunningBalances(
  txns: ImList<CashFlowTransaction>,
  sortBy: string,
  sortOrder: string,
  endingBalance: number,
  isExcludePendingTxn: boolean,
): ImList<CashFlowTransaction> {
  if (sortBy !== 'date') {
    return txns;
  }

  let txnsWithBalances: CashFlowTransaction[] = [];
  let balance = endingBalance;
  const reverse = sortOrder === 'ascending';
  const parseList = reverse ? txns.reverse() : txns;

  parseList.forEach((txn: CashFlowTransaction) => {
    if ((isExcludePendingTxn && isBankOwnedPending(txn)) || isC2RTransaction(txn)) {
      txnsWithBalances.push(txn);
    } else {
      txnsWithBalances.push(txn.set('balance', balance));
      balance -= noNaN(txn.amount);
    }
  });
  if (reverse) {
    txnsWithBalances = txnsWithBalances.reverse();
  }

  return ImList(txnsWithBalances);
}

export function padBounds(balances: number[]) {
  const firstMin: number = Math.min(...balances) || 0;
  const firstMax: number = Math.max(...balances) || 100;
  const deviation: number = (firstMax - firstMin) || Math.abs(firstMax) / 4 || 100;
  let minBalance: number = firstMin;
  let maxBalance: number = firstMax;
  if (minBalance > 0) {
    minBalance -= (minBalance * 0.4);
  } else {
    minBalance -= (deviation * 0.2);
  }

  if (maxBalance > 0) {
    maxBalance += deviation * 0.3;
  } else {
    maxBalance -= (maxBalance * 0.4);
  }

  const minLimit = firstMin - deviation;
  if (minBalance < minLimit) {
    minBalance = minLimit;
  }
  const maxLimit = firstMax + deviation;
  if (maxBalance > maxLimit) {
    maxBalance = maxLimit;
  }

  return {
    min: minBalance,
    max: maxBalance,
    high: firstMax,
    low: firstMin,
  };
}

/*
 FILTER FUTURE OR DUE OVERDUE
*/
export function filterFutureOrDueOverdue(txn: CashFlowTransaction) {
  const info = getTxnStateInfo(txn);
  if (info.status === 'DUE' || info.status === 'OVERDUE') {
    return true;
  }
  const refundState = getRefundState(txn);
  if (refundState === RefundState.EXPECTED || refundState === RefundState.OVERDUE) {
    return true;
  }
  return DateTime.fromISO(txn.postedOn).startOf('day') > DateTime.local().startOf('day');
}

export const getFutureTxnsForAccountIdsHelper = (props: TransactionListUtilProps) => {
  const {
    accountIdsList,
    accountsById,
    transactionsByAccountId,
    transactionsByAccountIdsSelectors,
    featureFlags,
  } = props;
  let txnList: ImList<CashFlowTransaction> = ImList();
  let filteredTransactions: CashFlowTransaction[];
  const filter: string = '';
  const sortBy: string = 'date';
  const sortOrder: string = 'ascending';

  accountIdsList.forEach((accountId: string) => {
    const acct: Account | undefined = accountsById.get(accountId);
    if (acct && !(featureFlags.get('hideLoanTransactions') && acct.type === 'LOAN')) {
      const transactionsForAccountIdSelector = transactionsByAccountIdsSelectors.get(accountId);
      const transactionsForAccountId =
        transactionsForAccountIdSelector(transactionsByAccountId, {
          sortBy,
          sortOrder,
          filter,
        });
      filteredTransactions = transactionsForAccountId.filter((txn) => filterFutureOrDueOverdue(txn));
      txnList = txnList.concat(filteredTransactions);
    }
  });

  return txnList;
};

/*
  Get Future Transactions helper function
 */
export const getFutureTxnsForAccountIds = (props: TransactionListUtilProps) => {
  const {
    accountIdsList,
    accountsById,
    transactionsByAccountId,
    transactionsByAccountIdsSelectors,
    featureFlags,
  } = props;
  
  const key = getOrCreateStringFromObject(
    'futureTxnsForAccountIds',
    {
      accountIdsList,
      accountsById,
      featureFlags,
      transactionsByAccountId,
      transactionsByAccountIdsSelectors,
    },
    5,
    memoizeWithKeyRetrieveCache,   // fn used for garbage collection for unusued object strings
  );

  return memoizeWithKey(
    'futureTxnsForAccountIds',
    () => getFutureTxnsForAccountIdsHelper(props),
    key,
    5,
  );
};

export const isRecentSpend = (
  txnList: ImList<CashFlowTransaction>,
  categoriesById: ImMap<string, Category | string>,
): ImList<CashFlowTransaction> =>
  txnList.filter((txn: CashFlowTransaction) => {
    if (!transactionsUtils.isSplitTxn(txn) && txn.coa) {  // ensure there is a coa regardless
      // returns if is matched scheduled txn or expense
      let cat = categoriesById.get(txn.coa.id);
      const coaType = txn.coa.type;
      if (!cat) cat = 'UNCATEGORIZED';
      if (txn.isExcludedFromReports) {
        return false;
      }
      if (
        typeof cat === 'object' &&
        (cat.type === 'EXPENSE' || 'UNCATEGORIZED') &&
        cat.type !== 'INCOME' &&
        coaType !== 'ACCOUNT' &&
        coaType !== 'BALANCE_ADJUSTMENT'
      ) {
        return !(txn.source && txn.source === 'SCHEDULED_TRANSACTION_PENDING');
      }
    } else {
      // Check twice in case is split and scheduled, who knows weirder things have happened
      return !(txn.source && txn.source === 'SCHEDULED_TRANSACTION_PENDING');
    }
    return false;
  });
